import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navLinks } from "@/mocks/homeData";
import LanguageSwitcher from "./LanguageSwitcher";
import LocalizedNavLabel from "./LocalizedNavLabel";

interface NavLink {
  label: string;
  href: string;
  external?: boolean;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNavClick = (link: NavLink) => {
    if (link.external) {
      window.open(link.href, "_blank", "noopener,noreferrer");
      return;
    }
    if (link.href.startsWith("/")) {
      window.scrollTo(0, 0);
      navigate(link.href);
    }
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/" || location.pathname === "/home";
    return location.pathname.startsWith(href);
  };

  const typedNavLinks = navLinks as NavLink[];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-out ${
        scrolled
          ? "py-1"
          : "py-2"
      }`}
      style={
        scrolled
          ? {
              background: "rgba(10, 10, 10, 0.20)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }
          : {
              background: "transparent",
            }
      }
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between mx-auto transition-all duration-300 ease-out ${
            scrolled ? "h-14" : "h-16 md:h-18"
          }`}
        >
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            className="flex items-center gap-2.5 cursor-pointer group shrink-0"
            aria-label="千華駐科技首頁"
          >
            <img
              src="https://storage.readdy-site.link/project_files/6517ebfd-0186-45e8-a53b-6a7dda87aef3/6d23f5d8-f2c4-4070-a39b-51782f265262_compressed_logo-02.webp"
              alt="千華駐科技"
              className={`object-contain h-auto w-auto transition-all duration-300 ease-out ${
                scrolled ? "max-h-4 max-w-[72px]" : "max-h-6 max-w-[100px]"
              }`}
            />
            <span
              className={`font-heading font-bold tracking-wide transition-all duration-300 ease-out whitespace-nowrap ${
                scrolled ? "text-sm" : "text-lg md:text-xl"
              } text-background-100`}
            >
              千華駐科技出版有限公司
            </span>
          </a>

          <div className="hidden lg:flex items-center gap-1">
            {typedNavLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`relative px-3 py-2 text-sm font-bold rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 ${
                  isActive(link.href)
                    ? "text-background-100 bg-white/20"
                    : "text-background-200 hover:text-background-100 hover:bg-white/12"
                }`}
              >
                <LocalizedNavLabel href={link.href} fallback={link.label} />
                {link.external && (
                  <i className="ri-external-link-line text-[10px] opacity-70 ml-0.5" />
                )}
              </button>
            ))}
          </div>

          <LanguageSwitcher />

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors shrink-0 text-background-200 hover:bg-white/15"
            aria-label="選單"
          >
            <i className={`text-lg ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden"
          style={
            scrolled
              ? {
                  background: "rgba(10, 10, 10, 0.2)",
                  backdropFilter: "blur(24px)",
                  WebkitBackdropFilter: "blur(24px)",
                }
              : {
                  background: "rgba(10, 10, 10, 0.2)",
                }
          }
        >
          <div className="px-4 py-3 flex flex-col gap-1">
            {typedNavLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className={`text-left px-3 py-2.5 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1 ${
                  isActive(link.href)
                    ? "text-background-100 bg-white/20"
                    : "text-background-300 hover:text-background-100 hover:bg-white/10"
                }`}
              >
                <LocalizedNavLabel href={link.href} fallback={link.label} />
                {link.external && (
                  <i className="ri-external-link-line text-[10px] opacity-70" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}